import {
  AXIOME_REST,
  AXM_DENOM,
  applyBps,
  applyBpsCeil,
  DEFAULT_SLIPPAGE_BPS,
  msgExecuteContract,
  toMicro,
  type CosmosMsg,
} from "./types.js";

export type VaultConfig = {
  vaultAddress: string;
  restUrl?: string;
};

export type MarketView = {
  symbol: string;
  pool: string;
  token: string;
  token_decimals: number;
  active: boolean;
  inventory: string;
  total_lp: string;
  total_axm_deposited: string;
  reward_budget_token?: string;
  treasury_pending_token?: string;
  matched?: string;
};

async function smartQuery<T>(
  rest: string,
  contract: string,
  query: Record<string, unknown>,
): Promise<T> {
  const encoded =
    typeof btoa === "function"
      ? btoa(JSON.stringify(query))
      : Buffer.from(JSON.stringify(query), "utf8").toString("base64");
  const url = `${rest.replace(/\/$/, "")}/cosmwasm/wasm/v1/contract/${contract}/smart/${encoded}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`query failed: ${res.status}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export class CounterLpVault {
  readonly vault: string;
  readonly rest: string;

  constructor(cfg: VaultConfig) {
    this.vault = cfg.vaultAddress;
    this.rest = cfg.restUrl ?? AXIOME_REST;
  }

  async config() {
    return smartQuery<{
      admin: string;
      treasury: string;
      depositor_pool_share: string;
      depositor_community_share: string;
      supplier_of_platform_share?: string;
      min_supply_axm?: string;
      allowed_locks?: number[];
      axm_denom: string;
      max_provide_bps?: number;
      max_harvest_profit_bps?: number;
      min_harvest_interval_secs?: number;
      max_lots_per_market?: number;
      harvest_slippage_bps?: number;
      min_pool_axm_liquidity?: string;
    }>(this.rest, this.vault, { config: {} });
  }

  async markets() {
    const pageSize = 50;
    const all: MarketView[] = [];
    let startAfter: string | undefined;
    for (let i = 0; i < 40; i++) {
      const res = await smartQuery<{ markets: MarketView[] }>(this.rest, this.vault, {
        markets: { limit: pageSize, start_after: startAfter ?? null },
      });
      const batch = res.markets ?? [];
      if (!batch.length) break;
      all.push(...batch);
      if (batch.length < pageSize) break;
      startAfter = batch[batch.length - 1]?.symbol;
      if (!startAfter) break;
    }
    return all;
  }

  async preview(symbol: string, axmHuman: string) {
    return smartQuery<{
      axm_amount: string;
      token_needed: string;
      inventory: string;
      estimated_lp: string;
      can_provide: boolean;
    }>(this.rest, this.vault, {
      preview_provide: {
        symbol,
        axm_amount: toMicro(axmHuman),
      },
    });
  }

  async previewWithdraw(owner: string, symbol: string, shareBps = 10_000) {
    return smartQuery<{
      lp_out: string;
      estimated_axm: string;
      estimated_token: string;
      min_axm_suggested: string;
      min_token_suggested: string;
      axm_principal: string;
      token_principal: string;
    }>(this.rest, this.vault, {
      preview_withdraw: { owner, symbol, share_bps: shareBps },
    });
  }

  async position(owner: string, symbol: string) {
    return smartQuery<{
      owner: string;
      symbol: string;
      lp_amount: string;
      shares?: string;
      axm_deposited: string;
      token_matched: string;
      pending_pool_rewards: string;
      pending_community_rewards: string;
      pending_token_rewards?: string;
    }>(this.rest, this.vault, { position: { owner, symbol } });
  }

  async positions(owner: string) {
    const pageSize = 50;
    type Pos = {
      symbol: string;
      lp_amount: string;
      shares?: string;
      axm_deposited: string;
      token_matched: string;
      pending_pool_rewards: string;
      pending_community_rewards: string;
      pending_token_rewards?: string;
    };
    const all: Pos[] = [];
    let startAfter: string | undefined;
    for (let i = 0; i < 40; i++) {
      const res = await smartQuery<{ positions: Pos[] }>(this.rest, this.vault, {
        positions: { owner, limit: pageSize, start_after: startAfter ?? null },
      });
      const batch = res.positions ?? [];
      if (!batch.length) break;
      all.push(...batch);
      if (batch.length < pageSize) break;
      startAfter = batch[batch.length - 1]?.symbol;
      if (!startAfter) break;
    }
    return all;
  }

  async pendingRewards(owner: string, symbol: string) {
    return smartQuery<{
      pool_rewards: string;
      community_rewards: string;
      total: string;
      token_rewards?: string;
      depositor_pool_share: string;
      depositor_community_share: string;
    }>(this.rest, this.vault, { pending_rewards: { owner, symbol } });
  }

  /** @deprecated Prefer buildProvideMsgWithSlippage — zeros are rejected on-chain anyway. */
  buildProvideMsg(params: {
    sender: string;
    symbol: string;
    axmHuman: string;
    minLp: string;
    maxToken: string;
  }): CosmosMsg {
    if (!params.minLp || params.minLp === "0") {
      throw new Error("Сумма слишком мала — увеличьте вклад");
    }
    if (!params.maxToken || params.maxToken === "0") {
      throw new Error("Сумма слишком мала — увеличьте вклад");
    }
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: {
        provide: {
          symbol: params.symbol,
          min_lp: params.minLp,
          max_token: params.maxToken,
        },
      },
      funds: [{ denom: AXM_DENOM, amount: toMicro(params.axmHuman) }],
    });
  }

  /**
   * Build provide with default 1% slippage.
   * Does NOT prepend Harvest by default — a redundant Harvest inside
   * min_harvest_interval caused wallet simulation "Load Failed".
   * Pass `withHarvest: true` only when the chain returned HarvestRequired.
   */
  async buildProvideMsgWithSlippage(params: {
    sender: string;
    symbol: string;
    axmHuman: string;
    slippageBps?: number;
    withHarvest?: boolean;
  }): Promise<CosmosMsg[]> {
    const slip = params.slippageBps ?? DEFAULT_SLIPPAGE_BPS;
    const preview = await this.preview(params.symbol, params.axmHuman);
    if (!preview.can_provide) {
      throw new Error("Сейчас внесение временно недоступно — попробуйте позже или другую сумму");
    }
    if (slip < 0 || slip > 2_000) {
      throw new Error("slippageBps must be 0..=2000");
    }
    const minLp = applyBps(preview.estimated_lp, 10_000 - slip);
    const maxToken = applyBpsCeil(preview.token_needed, 10_000 + slip);
    if (!minLp || minLp === "0") {
      throw new Error("Сумма слишком мала или рынок изменился — попробуйте другую сумму");
    }
    const provide = this.buildProvideMsg({
      sender: params.sender,
      symbol: params.symbol,
      axmHuman: params.axmHuman,
      minLp,
      maxToken,
    });
    if (params.withHarvest) {
      return [
        this.buildHarvestMsg({ sender: params.sender, symbol: params.symbol }),
        provide,
      ];
    }
    return [provide];
  }

  buildWithdrawMsg(params: {
    sender: string;
    symbol: string;
    shareBps: number;
    minAxm: string;
    minToken: string;
  }): CosmosMsg {
    if (!params.minAxm || params.minAxm === "0") {
      throw new Error("Вывод временно недоступен — попробуйте через минуту");
    }
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: {
        withdraw: {
          symbol: params.symbol,
          share_bps: params.shareBps,
          min_axm: params.minAxm,
          min_token: params.minToken || "0",
        },
      },
    });
  }

  /** Build withdraw using preview_withdraw mins. Fails closed if preview unavailable. */
  async buildWithdrawMsgWithSlippage(params: {
    sender: string;
    symbol: string;
    shareBps?: number;
  }): Promise<CosmosMsg[]> {
    const shareBps = params.shareBps ?? 10_000;
    const preview = await this.previewWithdraw(params.sender, params.symbol, shareBps);
    if (!preview.min_axm_suggested || preview.min_axm_suggested === "0") {
      throw new Error("Вывод временно недоступен — попробуйте через минуту");
    }
    const minToken =
      preview.min_token_suggested && preview.min_token_suggested !== "0"
        ? preview.min_token_suggested
        : "1";
    return [
      this.buildWithdrawMsg({
        sender: params.sender,
        symbol: params.symbol,
        shareBps,
        minAxm: preview.min_axm_suggested,
        minToken,
      }),
    ];
  }

  buildClaimMsg(params: { sender: string; symbol?: string }): CosmosMsg {
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: {
        claim_rewards: { symbol: params.symbol ?? null },
      },
    });
  }

  /**
   * Claim. On-chain ClaimRewards already auto-harvests when needed —
   * do not prepend standalone Harvest (triggers Load Failed inside the interval).
   */
  async buildClaimMsgs(params: {
    sender: string;
    symbol?: string;
    symbols?: string[];
  }): Promise<CosmosMsg[]> {
    return [this.buildClaimMsg({ sender: params.sender, symbol: params.symbol })];
  }

  buildRegisterMarketMsg(params: {
    sender: string;
    symbol: string;
    pool: string;
    token: string;
    tokenDecimals: number;
  }): CosmosMsg {
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: {
        register_market: {
          symbol: params.symbol,
          pool: params.pool,
          token: params.token,
          token_decimals: params.tokenDecimals,
        },
      },
    });
  }

  buildHarvestMsg(params: { sender: string; symbol: string }): CosmosMsg {
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: { harvest: { symbol: params.symbol } },
    });
  }

  buildFundCommunityRewardsMsg(params: {
    sender: string;
    symbol: string;
    axmHuman: string;
  }): CosmosMsg {
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: { fund_community_rewards: { symbol: params.symbol } },
      funds: [{ denom: AXM_DENOM, amount: toMicro(params.axmHuman) }],
    });
  }

  buildWithdrawTreasuryMsg(params: {
    sender: string;
    amountHuman?: string;
    recipient?: string;
  }): CosmosMsg {
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: {
        withdraw_treasury: {
          amount: params.amountHuman ? toMicro(params.amountHuman) : null,
          recipient: params.recipient ?? null,
        },
      },
    });
  }

  buildSetMarketStatusMsg(params: {
    sender: string;
    symbol: string;
    active: boolean;
  }): CosmosMsg {
    return msgExecuteContract({
      sender: params.sender,
      contract: this.vault,
      msg: {
        set_market_status: { symbol: params.symbol, active: params.active },
      },
    });
  }
}
